<template>
    <card-skeleton class-name="ma-0 pa-1" :is-loading="isLoading">
        <v-card-item>
            <v-form ref="form" @submit.prevent="handleSubmit">
                <form-title :text="t('inquiry.newMessage')" />
                <input-field
                    id="subject"
                    v-model="formData.subject"
                    :label="t('form.subject')"
                    :required="true"
                    :disabled="isSubmitting"
                    @input="validateOnInput"
                />
                <text-area-field
                    id="body"
                    v-model="formData.body"
                    :counter="TEXT_AREA_SETTINGS.LENGTH"
                    :max="TEXT_AREA_SETTINGS.LENGTH"
                    :persistent-counter="true"
                    class="mt-2 mb-2"
                    :rows="TEXT_AREA_SETTINGS.ROW"
                    :row-height="TEXT_AREA_SETTINGS.ROW_HEIGHT"
                    :label="t('form.message')"
                    :required="true"
                    :disabled="isSubmitting"
                    @input="validateOnInput"
                />
                <file-upload
                    id="attachments"
                    v-model="files"
                    :show-size="true"
                    :clearable="true"
                    :disabled="isSubmitting"
                    :title="t('form.selectFile')"
                />
                <v-card-actions class="d-flex justify-end ma-0 pa-0">
                    <card-button class="flex-1-0-0" size="large" :inactive-label="t('form.cancel')" @click="handleCancel">
                        {{ t('form.cancel') }}
                    </card-button>
                    <card-button
                        type="submit"
                        class="flex-1-0-0"
                        variant="flat"
                        :disabled="!isFormValid"
                        size="large"
                        :inactive-label="t('inquiry.sendMessage')"
                        :loading="isSubmitting"
                    >
                        {{ t('inquiry.sendMessage') }}
                    </card-button>
                </v-card-actions>
            </v-form>

            <notification-bar
                v-model:show="notificationBarState.show"
                :color="notificationBarState.color"
                :icon="notificationBarState.icon"
                :border="notificationBarState.border"
                :message="notificationBarState.message"
            />
        </v-card-item>
    </card-skeleton>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { CreateIssueCommentDto, ICreateIssueComment } from '../../../types';
import { createIssueComment } from '../../../services/api';
import CardButton from '@/features/common/components/CardButton.vue';
import { InputField, FormTitle, NotificationBar, TextAreaField, FileUpload } from '@/features/common/components/form';
import { TEXT_AREA_SETTINGS } from '@/features/Issues/constants';
import { useFormHandling, useNotification, useFormData } from '@/features/common/components/form/composables';
import { useMainStore } from '@/features/common/stores';
import { useIssueCommentStore } from '@/features/Issues/store/cardStore';
import CardSkeleton from '@/features/common/components/CardSkeleton.vue';

const props = defineProps<ICreateIssueComment>();
const emit = defineEmits(['update:isVisible', 'submit-success', 'cancel']);

const { t } = useI18n();

const initialFormState: CreateIssueCommentDto = {
    subject: '',
    body: '',
    attachments: []
};

const { form, isSubmitting, isFormValid, toggleConfirmation, toggleIsSubmitting, isFormEmpty, validateOnInput, resetFormValidation } =
    useFormHandling();
const { notificationBarState, notificationSuccess, notificationError } = useNotification();
const { formData, resetFormData, files, mappedFileAttachments } = useFormData(initialFormState);
const { setItems } = useIssueCommentStore();
const mainStore = useMainStore();

const saveData = async (issueId: number, comment: CreateIssueCommentDto) => {
    try {
        const newIssue = await createIssueComment(issueId, { ...comment, attachments: mappedFileAttachments() });
        setItems(newIssue?.comments);
        notificationBarState.value = notificationSuccess(t('inquiry.commentAdded'));
        return true;
    } catch (error: any) {
        notificationBarState.value = notificationError(t('inquiry.commentAddFailed'));
        mainStore.registerError(error);
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleConfirmation(false);
        toggleIsSubmitting(true);
        const result = await saveData(props.issueId, formData.value);
        if (result) {
            resetFormData(form);
            emit('submit-success');
        }
    } catch (error) {
        console.error(error);
    } finally {
        toggleIsSubmitting(false);
        emit('update:isVisible', false);
    }
};

const handleSubmit = async () => {
    if (isFormEmpty()) return;
    if (!isFormValid) return;
    await submitForm();
};

const handleCancel = () => {
    resetFormData(form);
    resetFormValidation();
    toggleConfirmation(false);
    emit('cancel');
    emit('update:isVisible', false);
};
</script>
